'use client';

import {
  Button,
  Center,
  Heading,
  Inline,
  Input,
  Stack,
  Text,
} from '@cortex/ui';
import { Check } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

import { OPTION_BUTTON_BASE, QUESTION_ENTER_ANIMATION } from './styles';
import type { MultiSelectQuestionProps } from './types';

export function MultiSelectQuestion({
  acknowledgment,
  question,
  options,
  allowOther = false,
  minSelections = 1,
  maxSelections,
  onSubmit,
}: MultiSelectQuestionProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [otherText, setOtherText] = useState('');
  const isOtherSelected = selectedIds.has('__other__');

  const toggleOption = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        // Check max selections
        if (maxSelections && next.size >= maxSelections) {
          return prev;
        }
        next.add(id);
      }
      return next;
    });
  };

  const handleSubmit = () => {
    const selections = Array.from(selectedIds).filter(
      (id) => id !== '__other__'
    );
    const other =
      isOtherSelected && otherText.trim() ? otherText.trim() : undefined;
    onSubmit(selections, other);
  };

  const isValid =
    selectedIds.size >= minSelections &&
    (!isOtherSelected || otherText.trim().length > 0);

  const canSelectMore = !maxSelections || selectedIds.size < maxSelections;

  return (
    <Stack gap="lg" className={QUESTION_ENTER_ANIMATION}>
      {acknowledgment && (
        <Text color="muted" size="sm" className="italic">
          {acknowledgment}
        </Text>
      )}

      <Stack gap="sm">
        <Heading level={3} className="text-lg font-medium">
          {question}
        </Heading>
        <Text color="muted" size="sm">
          {maxSelections
            ? `Select ${minSelections === maxSelections ? maxSelections : `${minSelections}-${maxSelections}`} options`
            : minSelections > 1
              ? `Select at least ${minSelections} options`
              : 'Select all that apply'}
        </Text>
      </Stack>

      <Stack gap="sm">
        {options.map((option) => {
          const isSelected = selectedIds.has(option.id);
          const isDisabled = !isSelected && !canSelectMore;

          return (
            <Button
              key={option.id}
              type="button"
              variant="outline"
              onClick={() => toggleOption(option.id)}
              disabled={isDisabled}
              className={cn(
                OPTION_BUTTON_BASE,
                isDisabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:border-primary/50 hover:bg-accent/5',
                isSelected ? 'border-primary bg-primary/5' : 'border-border'
              )}
            >
              <Inline gap="sm" align="start">
                <Center
                  className={cn(
                    'mt-0.5 h-5 w-5 shrink-0 rounded border-2 transition-colors',
                    isSelected
                      ? 'border-primary bg-primary'
                      : 'border-muted-foreground/30'
                  )}
                >
                  {isSelected && (
                    <Check className="text-primary-foreground h-3 w-3" />
                  )}
                </Center>
                <Stack gap="xs" className="flex-1">
                  <Text as="span" weight="medium">
                    {option.label}
                  </Text>
                  {option.description && (
                    <Text color="muted" size="sm">
                      {option.description}
                    </Text>
                  )}
                </Stack>
              </Inline>
            </Button>
          );
        })}

        {allowOther && (
          <Stack
            gap="none"
            className={cn(
              'rounded-lg border p-4 transition-all',
              isOtherSelected ? 'border-primary bg-primary/5' : 'border-border',
              !canSelectMore && !isOtherSelected
                ? 'cursor-not-allowed opacity-50'
                : ''
            )}
          >
            <Button
              type="button"
              variant="ghost"
              onClick={() => toggleOption('__other__')}
              disabled={!canSelectMore && !isOtherSelected}
              className="w-full text-left"
            >
              <Inline gap="sm" align="start">
                <Center
                  className={cn(
                    'mt-0.5 h-5 w-5 shrink-0 rounded border-2 transition-colors',
                    isOtherSelected
                      ? 'border-primary bg-primary'
                      : 'border-muted-foreground/30'
                  )}
                >
                  {isOtherSelected && (
                    <Check className="text-primary-foreground h-3 w-3" />
                  )}
                </Center>
                <Text as="span" weight="medium">
                  Other (please specify)
                </Text>
              </Inline>
            </Button>

            {isOtherSelected && (
              <Stack className="animate-in fade-in slide-in-from-top-2 mt-3 duration-200">
                <Input
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  placeholder="Please describe..."
                  autoFocus
                />
              </Stack>
            )}
          </Stack>
        )}
      </Stack>

      <Button
        onClick={handleSubmit}
        disabled={!isValid}
        size="lg"
        className="w-full"
      >
        Continue
      </Button>
    </Stack>
  );
}
